import * as React from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';

import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { BaseHeroContent, Hero } from '../../../ui/hero/Index';

import { Textarea } from '../../../ui/form/Textarea';
import { MAIL_MUTATION } from '../../../../../client/__graphql__/mutations/mail';
import { Mail, MailVariables } from '../../../../__types__/generated/Mail';

const SelgeKjopeFritidsbolig: React.FC = () => {
  const location = useLocation();
  const { routeConfig } = React.useContext(SiteContext);

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [mail, { error }] = useMutation<Mail, MailVariables>(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead.mail?.success) {
        setPosted(true);
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'textarea',
        name: 'comment',
        placeholder: 'Eks. kommentar',
        value: '',
        label: 'Kommentar'
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);

      mail({
        variables: {
          input: {
            ...preparedFields,
            referer: `${location.pathname}${location.search}`,
            leadType: 'EPOST',
            mailType: 18
          }
        }
      });
    }
  });

  return (
    <>
      <Seo
        title="Vurderer du å selge eller kjøpe fritidseiendom? - Privatmegleren Sædberg & Lian"
        description="Privatmegleren Sædberg og Lian er nå godt i gang med å planlegge sesongen for salg av hytter og leiligheter ved sjøen som skal ut for salg i år."
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/content/sedberglian/bilde_sedberg.jpeg"
        opacity={0.4}
        height="600px"
        className="top-header-margin"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Vurderer du å selge eller kjøpe fritidseiendom?
          </Heading>
          <Paragraph center={true}>
            Privatmegleren Sædberg og Lian er nå godt i gang med å planlegge
            sesongen for salg av hytter og leiligheter ved sjøen som skal ut for
            salg i år.
          </Paragraph>
        </HeroContent>
      </Hero>
      <Container
        className="padding-top"
        style={{ ...pageFadeIn, paddingTop: '40px' }}
      >
        <Paragraph center>
          Vår oppgave er å oppnå resultater for deg når du er i en situasjon der
          mye står på spill. Om du skal kjøpe eller selge, hjelper vi deg med
          strategi, taktisk planlegging og gjennomføring. Med meglere
          håndplukket blant de beste i markedet og innovative bransjeledende
          løsninger, gir vi deg et fortrinn i eiendomssalget.
        </Paragraph>
        <Paragraph center>
          Vårt mål er å gi våre kunder en opplevelse utover det som forventes,
          og å oppfylle drømmen om fritidseiendom. Hele 9 av 10 kunder velger
          oss på anbefaling fra tidligere kunder, og er en bekreftelse på at
          våre meglere leverer en kundeopplevelse og resultat som overgår dine
          forventninger.
        </Paragraph>
        <Paragraph center>
          Privatmegleren Sædberg og Lian er spesialister for fritidseiendommer
          ved sjøen på Sørlandet. Vårt hytteteam har lang erfaring med salg av
          eiendommer og vårt distrikt for salg av fritidseiendommer er Grimstad,
          Lillesand, Kristiansand, Søgne, Mandal, Lindesnes, Farsund og
          Flekkefjord.
        </Paragraph>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h2" center={true}>
              Selge hytte?
            </Heading>
          </Section>
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item: any, index: number) => {
              if (item.type === 'textarea') {
                return (
                  <FormRow key={`field_${index}`}>
                    <Textarea
                      name={item.name}
                      label={item.label}
                      placeholder={item.placeholder}
                      value={item.value}
                      onChange={handleChange}
                      error={item.error}
                    />
                  </FormRow>
                );
              }

              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Send
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default SelgeKjopeFritidsbolig;

const FormWrapper = styled(animated.div)``;

const HeroContent = styled(animated.div)`
  ${BaseHeroContent}
  flex-flow: column;
  max-width: 960px;
`;
